// =============================================================================
// Activate Account Page - Sarsen Capital Partners Investor Portal
// Design: Sarsen "Strata" identity (2026-09) via the shared AuthShell layout
// Flow: User clicks activation link from welcome email -> creates password -> login
// =============================================================================

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import { getApiError } from '../../services/api';
import {
  AuthShell,
  AuthHeading,
  AuthError,
  AuthButton,
  AuthLink,
  AuthCheckItem,
  AuthSuccessBadge,
  authInputClass,
  authInputIconButtonClass,
} from '../../components/layout/AuthShell';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// =============================================================================
// Component
// =============================================================================

export function ActivateAccountPage() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // ===========================================================================
  // Password Validation
  // ===========================================================================

  const passwordChecks = [
    { check: password.length >= 12, text: 'At least 12 characters' },
    { check: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { check: /[a-z]/.test(password), text: 'One lowercase letter' },
    { check: /[0-9]/.test(password), text: 'One number' },
    { check: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password), text: 'One special character' },
  ];

  const isPasswordValid = passwordChecks.every(c => c.check);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Invalid activation link. Please use the link from your welcome email.');
      return;
    }

    if (!isPasswordValid) {
      setError('Please fix password requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.activateAccount(token, password);

      if (result.success) {
        setIsSuccess(true);
      } else {
        setError(result.error || 'Failed to activate account');
      }
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <AuthShell caption="Account Activation">
      {!isSuccess ? (
        <div>
          <AuthHeading
            eyebrow="Account Activation"
            title="Create your password"
            subtitle="Choose a strong password to secure your investor portal account."
          />

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password Input */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                autoComplete="new-password"
                autoFocus
                className={`${authInputClass} pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={authInputIconButtonClass}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Password Requirements */}
            <div className="bg-white border border-brand-line p-4">
              <p className="m-0 mb-3 text-[11px] font-semibold tracking-[.2em] uppercase text-brand-muted">
                Password must contain
              </p>
              <ul className="m-0 p-0 list-none text-xs space-y-2">
                {passwordChecks.map(({ check, text }) => (
                  <AuthCheckItem key={text} met={check} text={text} />
                ))}
              </ul>
            </div>

            {/* Confirm Password Input */}
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                required
                autoComplete="new-password"
                className={`${authInputClass} pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className={authInputIconButtonClass}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Password Match Status */}
            {confirmPassword.length > 0 && (
              <p className={`m-0 text-xs ${passwordsMatch ? 'text-brand-navy' : 'text-brand-terracotta'}`}>
                {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
              </p>
            )}

            {error && <AuthError message={error} />}

            <AuthButton
              type="submit"
              disabled={isLoading || !isPasswordValid || !passwordsMatch}
            >
              {isLoading ? 'Creating…' : 'Create password'}
            </AuthButton>
          </form>

          <div className="mt-10 pt-6 border-t border-brand-line">
            <p className="m-0 text-sm text-brand-ink">
              Already have an account? <AuthLink to="/login">Sign in</AuthLink>
            </p>
          </div>
        </div>
      ) : (
        /* Success State */
        <div>
          <AuthSuccessBadge />

          <AuthHeading
            center
            title="Account activated"
            subtitle="Your password has been created. You can now sign in to access your data rooms."
          />

          <AuthButton type="button" onClick={() => navigate('/login')}>
            Sign in
          </AuthButton>
        </div>
      )}
    </AuthShell>
  );
}

export default ActivateAccountPage;
